import React, { useState, useRef } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import { toast } from 'react-toastify';

import {
  trackCheckInUploadFileSuccess,
  trackCheckInUploadFileError,
  trackCheckInBatchUploadVideoSuccess,
  trackCheckInUGCDetailsClickYes,
  trackCheckInUGCDetailsClickNo,
} from '../../../analytics';

import { InfoPoints } from '../../../components/custom';
import { Form, FormError } from '../../../components/form';
import { Button } from '../../../components/general';
import {
  ConfirmModal,
  ErrorModal,
  AssetPreviewModal,
} from '../../../components/modals';
import { DraggerSimplified } from '../../../components/video';

import { EMAILS } from '../../../constants';

import { sendEmail } from '../../../services/api/emails';
import { updatePersonVideos } from '../../../services/checkInService';

import { PublicEvent, Face, File } from '../../../types';
import { isEmpty } from '../../../utils';

import { validateForm } from '../components/checkInValidations';
import { getTipsList, getUgcInfoPoints } from '../components/getCheckinText';
import VideoBlock from '../components/videoBlock';

interface Props {
  event: PublicEvent;
  face: Face;
  handleAfterUpload?: () => void;
  refetchFace?: () => void;
  reqAssets?: string;
  isPreview?: boolean;
}

const VideoStage: React.FC<Props> = (props) => {
  const { event, face, handleAfterUpload, refetchFace, reqAssets, isPreview } =
    props;

  const uploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPreviewVideo, setShowPreviewVideo] = useState(false);
  const [previewVideo, setPreviewVideo] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const isPortrait = event.videoMode === 'portrait';

  const requiredShots =
    reqAssets?.split('-')[1].split(',').map(Number) ?? event.wildcards ?? [];

  const handleUpload = (file: UploadFile, fileList: UploadFile[]) => {
    // clears any pending batch triggers
    if (uploadTimeoutRef.current) clearTimeout(uploadTimeoutRef.current);

    // sets a timeout so it waits until all beforeUpload calls finish
    uploadTimeoutRef.current = setTimeout(() => handleSubmit(fileList), 0);

    return false;
  };

  const handleSubmit = async (fileList: UploadFile[]) => {
    if (isPreview) return;

    setIsProcessing(true);
    setShowConfirmModal(true);

    try {
      const { videoCount } = await updatePersonVideos({
        eventId: event.eventId,
        parentElementId: face.elementId,
        videos: fileList,
        trackOnSuccess: () => trackCheckInUploadFileSuccess('video'),
        trackOnError: (fileName: string) =>
          trackCheckInUploadFileError('video', fileName),
        trackOnBatchSuccess: (videoCount: number) =>
          trackCheckInBatchUploadVideoSuccess(videoCount),
      });

      if (!reqAssets) {
        const emailData = {
          recipientEmail: face.email,
          templateId: EMAILS.UGC_RECEIVED,
          dynamicTemplateData: {
            event_name: event.name,
            share_url: window.location.href,
          },
        };

        await sendEmail(emailData);
      }

      toast.success('Mission accomplished: videos uploaded');

      refetchFace && refetchFace();

      // if not enough videos uploaded – hide modal
      if (videoCount + (face.videos?.length ?? 0) < requiredShots.length)
        setShowConfirmModal(false);
      // else – if enough and it's MA page – hide modal and trigger after upload actions
      else if (!!reqAssets) {
        setShowConfirmModal(false);
        handleAfterUpload && handleAfterUpload();
      }
    } catch (err: any) {
      setError(err.message);
      setShowConfirmModal(false);
    } finally {
      setIsProcessing(false);
      setFormErrors({});
    }
  };

  const handleContinue = async () => {
    const validationErrors = await validateForm({
      type: 'video',
      formData: { videoFiles: face.videos },
      requiredShotsNum: requiredShots.length,
    });

    // if errors found – set errors and stop here
    if (!isEmpty(validationErrors)) {
      setFormErrors(validationErrors);
      return;
    }

    handleAfterUpload && handleAfterUpload();
  };

  const onHideConfirmModal = (hasNextStep: boolean) => {
    setShowConfirmModal(false);

    hasNextStep && handleAfterUpload && handleAfterUpload();
  };

  const togglePreviewVideo = (video: File | null) => {
    if (!!video) {
      setShowPreviewVideo(true);
      setPreviewVideo(video);
    } else {
      setShowPreviewVideo(false);
      setTimeout(() => setPreviewVideo(null), 500);
    }
  };

  const handleDetailsModal = (isAgree: boolean) => {
    onHideConfirmModal(isAgree);

    isAgree
      ? trackCheckInUGCDetailsClickYes()
      : trackCheckInUGCDetailsClickNo();
  };

  return (
    <React.Fragment>
      <Form formClassName="w-full">
        <h5>
          Upload videos of you
          <span className="text-primary-900">*</span>
        </h5>

        {face.videos && !isEmpty(face.videos) && (
          <React.Fragment>
            <h6>Uploaded videos ({face.videos.length})</h6>

            <div className="flex flex-wrap gap-4">
              {face.videos.map((video) => (
                <VideoBlock
                  video={video}
                  onClick={() => togglePreviewVideo(video)}
                  isPortrait={isPortrait}
                />
              ))}
            </div>

            <hr className="my-2" />

            <h6>Upload more videos</h6>
          </React.Fragment>
        )}

        <div>
          <DraggerSimplified
            type="video"
            heading={`Upload videos (${requiredShots.length})`}
            isMultiple
            onDrop={handleUpload}
            isPreview={isPreview}
          />

          <FormError errMsg={formErrors.videoFiles} />
        </div>

        <InfoPoints points={getUgcInfoPoints(event, requiredShots)} />

        {!reqAssets && (
          <Button onClick={handleContinue} className="w-full">
            Continue
          </Button>
        )}
      </Form>

      <AssetPreviewModal
        show={showPreviewVideo}
        onHide={() => togglePreviewVideo(null)}
        file={previewVideo}
        isVideo
        isPortrait={isPortrait}
      />

      <ConfirmModal
        show={showConfirmModal}
        isProcessing={isProcessing}
        heading={
          isProcessing
            ? "We're uploading your files"
            : `Great! You have successfully checked in to ${event.name}.`
        }
        subheading={
          !isProcessing && (
            <>
              <div>
                You may come back to this page to upload more videos later.
              </div>

              <div>
                Meanwhile, would you like to tell us a bit about yourself so we
                can personalise your experience even more?
              </div>
            </>
          )
        }
        tipsList={getTipsList()}
        btns={
          !isProcessing
            ? [
                { label: 'Yes', onClick: () => handleDetailsModal(true) },
                { label: 'No', onClick: () => handleDetailsModal(false) },
              ]
            : undefined
        }
      />

      <ErrorModal
        show={!!error}
        heading="Something went wrong"
        subheading={error}
        isTryAgainBtn
      />
    </React.Fragment>
  );
};

export default VideoStage;
